import type {
    CompRef,
    ConnectionList,
    ControllerConnectionItem,
    IConnectionItem,
    Pointer,
    PS
} from '@wix/document-services-types'
import experiment from 'experiment-amd'
import _ from 'lodash'
import connections from '../../connections/connections'
import dataModel from '../../dataModel/dataModel'
import hooks from '../../hooks/hooks'
import {registerHookWithPs} from '../../hooks/eventHooks/eventHooks'
import {COMPONENTS_HOOKS} from '@wix/document-manager-extensions/src/hooks'

import type {HookFunc} from '../../hooks/hooksHandlerFactory'
import appFilters from './appFilters'
import appFinder from './appFinder'
import type {AppData} from '@wix/platform-editor-sdk'
import workerService from '../services/workerService'
import type {DistributorExtensionAPI, LivePreviewSharedStateMessage} from '@wix/document-manager-extensions'
import {ReportableWarning} from '@wix/document-manager-utils'
import type {ComponentAfterRootAddDataFromExtEvent} from '@wix/document-manager-extensions/dist/src/extensions/components/hooks'

const notifyLivePreviewStateIfNeeded = (ps: PS, effectiveOptions) => {
    const shouldSync = _.get(effectiveOptions, ['sharedStateSyncOptions', 'shouldSync'])
    if (shouldSync) {
        ps.setOperationsQueue.runSetOperation(() => {
            const dataToSet = {
                livePreviewRefreshOptions: effectiveOptions
            }
            const {distributor} = ps.extensionAPI as DistributorExtensionAPI
            distributor.distributeMessageAfterApproval<LivePreviewSharedStateMessage>(
                'livePreviewSharedState',
                dataToSet
            )
        })
    }
}

const refreshAppsInternal = (ps: PS, effectiveOptions) => {
    let resolveRefresh: (value: Promise<void> | PromiseLike<Promise<void>>) => void = null

    const refreshPromise = new Promise<Promise<void>>(resolve => {
        resolveRefresh = resolve
    })

    // eslint-disable-next-line promise/prefer-await-to-then
    refreshPromise.then(() => {
        effectiveOptions.apps.forEach(appDefId => {
            workerService.notifyAppRefreshed(ps, appDefId)
        })
    })

    if (effectiveOptions.immediate && effectiveOptions.shouldRunInQueue !== false) {
        return ps.setOperationsQueue.runSetOperation(
            () => {
                resolveRefresh(ps.siteAPI.refreshAppsInCurrentPage(effectiveOptions))
            },
            [],
            {enforceType: 'GENERATE_AND_ENFORCE'}
        )
    }

    return resolveRefresh(ps.siteAPI.refreshAppsInCurrentPage(effectiveOptions))
}

const refreshAfterDataOrPropertyUpdate = (hookName: string, ps: PS, componentPointer: Pointer) => {
    if (appFilters.isBlocksAppWidget(ps, componentPointer)) {
        return
    }
    const controllersToRefresh = getComponentsControllers(ps, componentPointer, true)
    if (_.isEmpty(controllersToRefresh)) {
        return
    }
    const compData = dataModel.getDataItem(ps, componentPointer)
    const options = getOptionsWithAllAppsModifiers(ps, componentPointer, _.get(hooks.HOOKS, hookName))
    refreshWhenAppsNotEmpty(
        ps,
        appFinder.appsOfComp(ps, compData, componentPointer),
        hookName,
        componentPointer,
        _.assign({controllersToRefresh}, options)
    )
}

/**
 * @param {ps} ps
 * @param {Object} options
 * @param [originatingCmpPtr] - optional, used to customize options according to originating comp
 */
function refresh(ps: PS, options, originatingCmpPtr?: Pointer) {
    if (!isActive(ps)) {
        return
    }
    const diff = _.difference(options.apps, ps.siteAPI.getAllowedApps())
    if (diff.length) {
        ps.logger.captureError(
            new ReportableWarning({
                errorType: 'LivePreviewRefreshError',
                message: 'Live preview refresh contains not allowed apps',
                extras: {
                    allowedApps: ps.siteAPI.getAllowedApps(),
                    diff
                }
            })
        )
        options.apps = _.intersection(options.apps, ps.siteAPI.getAllowedApps())
    }
    options.apps = appFilters.addDependantApps(ps, _.uniq(options.apps))
    if (!options.skipAppsCheck && _.isEmpty(options.apps)) {
        return
    }
    const effectiveOptions = extendOptionsByApp(ps, options, originatingCmpPtr)
    refreshAppsInternal(ps, effectiveOptions)
    notifyLivePreviewStateIfNeeded(ps, effectiveOptions)
}

function refreshAppsAPI(ps: PS, {apps, source, shouldFetchData}) {
    const options = _.assign(appFilters.optionsModifierSelfRefresh(ps, apps), {
        source,
        shouldFetchData,
        sendInitAndStart: true,
        sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()}
    })

    if (!_.isEmpty(options.controllersToRefresh) && !_.isEmpty(options.compsIdsToReset)) {
        refresh(ps, options)
    }
}

function refreshComponentAppAPI(ps: PS, {componentsToReset, source, shouldFetchData}) {
    if (componentsToReset.length === 0) {
        return
    }

    const appControllerPairs = componentsToReset.map(componenentPointer => {
        const dataItem = dataModel.getDataItem(ps, componenentPointer)
        const apps = appFinder.appsOfComp(ps, dataItem, componenentPointer)
        const componentControllers = getComponentsControllers(ps, componenentPointer)

        return {apps, componentControllers}
    })

    const compsIdsToReset = componentsToReset.map(x => x.id)
    const apps = _.uniq(appControllerPairs.flatMap(x => x.apps))
    const controllersToRefresh = _.uniq(appControllerPairs.flatMap(x => x.componentControllers))

    const options = {
        apps,
        compsIdsToReset,
        controllersToRefresh,
        source,
        shouldFetchData,
        sendInitAndStart: true,
        sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()},
        resetRuntime: true
    }

    if (!_.isEmpty(options.controllersToRefresh) && !_.isEmpty(options.compsIdsToReset)) {
        refresh(ps, options)
    }
}

function updateWidgetViewState(ps: PS, options) {
    if (_.isEmpty(options) || _.isEmpty(options.compId)) {
        return
    }

    ps.setOperationsQueue.runSetOperation(() => {
        ps.siteAPI.triggerWidgetStateUpdate(options)
    })
}

function updateWidgetViewStateAfterRefresh(ps: PS, options) {
    if (!options?.componentRef) {
        return
    }

    ps.setOperationsQueue.runSetOperation(() => {
        ps.siteAPI.triggerWidgetStateUpdateAfterRefresh(options)
    })
}

function extendOptionsByApp(ps: PS, options, componentPointer: Pointer) {
    if (!options?.apps) {
        return options
    }
    return options.apps.reduce((res, app) => {
        const changeFromApp = _.get(appFilters.optionsModifiers, app, _.noop)(ps, componentPointer)
        return _.assign(res, changeFromApp)
    }, options)
}

function refreshWhenAppsNotEmpty(ps: PS, apps, source, componentPointer: Pointer, extraOptions) {
    if (componentPointer) {
        const componentPageId = _.get(ps.pointers.full.components.getPageOfComponent(componentPointer), 'id')
        const focusedPageId = ps.siteAPI.getFocusedRootId()
        if (componentPageId !== focusedPageId && componentPageId !== 'masterPage') {
            return
        }
    }
    const options = _.assign({apps, source, sendInitAndStart: true}, extraOptions || {})
    if (!_.isEmpty(apps)) {
        refresh(ps, options, componentPointer)
    }
}

function refreshAllApps(ps: PS, options) {
    refresh(ps, _.assign(options, {apps: ps.siteAPI.getAllowedApps()}))
}

function isActive(ps: PS) {
    const enabledEditors = {
        responsiveBlocks: true,
        'Editor1.4': true,
        wem: true,
        editor_x: true,
        studio: true,
        odeditor: true
    }
    return enabledEditors[ps.config.origin]
}

let registeredHooks: Record<string, HookFunc>

function registerHook(hookName: string, cb: HookFunc) {
    registeredHooks[hookName] = hooks.registerHook(hookName, cb)
}

function unregisterHooks() {
    _.forEach(registeredHooks, (hookValue, hookKey) => hooks.unregisterHook(hookKey, hookValue))
    registeredHooks = Object.create(null)
}

function getOptionsWithAllAppsModifiers(ps: PS, componentPointer: Pointer, hook: string) {
    const options = _.get(appFilters.optionsModifiersAllApps, hook, _.noop)(ps, componentPointer)
    _.assign(options, {sharedStateSyncOptions: getGenericSharedStateComponentOptions(ps, componentPointer)})
    return options
}

const getGenericSharedStateComponentOptions = (ps: PS, compPointer: Pointer) => ({
    shouldSync: true,
    pageId: _.get(ps.pointers.full.components.getPageOfComponent(compPointer), 'id')
})

function getComponentsControllers(ps: PS, componentPointer: Pointer, excludeOOIControllers?): string[] {
    const compControllers: string[] = []
    const componentType = ps.dal.get(ps.pointers.getInnerPointer(componentPointer, 'componentType'))
    const isIncludedOOIController = connections.isOOIController(ps, componentType) && !excludeOOIControllers
    if (connections.isControllerType(componentType) || isIncludedOOIController) {
        const controllerData = dataModel.getDataItem(ps, componentPointer)
        compControllers.push(controllerData.id)
    }
    const connectionItemPointer = dataModel.getConnectionsItemPointer(ps, componentPointer)
    if (!connectionItemPointer) {
        return compControllers
    }
    const componentConnections: ConnectionList = ps.dal.get(connectionItemPointer)
    if (!componentConnections?.items) {
        return compControllers
    }

    return _(componentConnections.items)
        .filter((item: IConnectionItem): item is ControllerConnectionItem => item.type === 'ConnectionItem')
        .flatMap(({controllerId}: ControllerConnectionItem) => {
            const pagePointer: CompRef = ps.pointers.components.getPageOfComponent(componentPointer)

            const controllerRef: Pointer = ps.pointers.platform.getControllerInPageByDataId(
                pagePointer,
                ps.pointers.components.isMobile(pagePointer),
                controllerId
            )

            if (!controllerRef) {
                return [controllerId]
            }

            return getComponentsControllers(ps, controllerRef, excludeOOIControllers)
        })
        .concat(compControllers)
        .uniq()
        .value()
}

function isDynamicPageDataChanged(pageInfo, prevPageInfo) {
    if (
        _.isObject(pageInfo) &&
        _.isObject(prevPageInfo) &&
        // @ts-expect-error
        prevPageInfo.routerDefinition &&
        // @ts-expect-error
        pageInfo.routerDefinition
    ) {
        return (
            // @ts-expect-error
            pageInfo.innerRoute === prevPageInfo.innerRoute &&
            // @ts-expect-error
            pageInfo.routerDefinition.routerId === prevPageInfo.routerDefinition.routerId &&
            // @ts-expect-error
            !_.isEqual(pageInfo.routerDefinition, prevPageInfo.routerDefinition)
        )
    }
    return false
}

const afterDataUpdate = (ps: PS, componentPointer: Pointer) => {
    refreshAfterDataOrPropertyUpdate('DATA.UPDATE_AFTER', ps, componentPointer)
}

const afterPropertiesUpdate = (ps: PS, componentPointer: CompRef) => {
    if (appFilters.isBlocksWidgetInnerComponent(ps, componentPointer)) {
        // Performance optimization:
        // there is no need to run live preview for Blocks widgets when properties are updated
        // so we just skip it in this case
        return
    }

    refreshAfterDataOrPropertyUpdate('PROPERTIES.UPDATE_AFTER', ps, componentPointer)
}

const afterConnectionsUpdate = (ps: PS, data, componentPointer: Pointer) => {
    const controllersToRefresh = getComponentsControllers(ps, componentPointer)
    const options = getOptionsWithAllAppsModifiers(ps, componentPointer, hooks.HOOKS.DATA.AFTER_UPDATE_CONNECTIONS)
    const dataItem = dataModel.getDataItem(ps, componentPointer)
    refreshWhenAppsNotEmpty(
        ps,
        appFinder.appsOfComp(ps, dataItem, componentPointer),
        'AFTER_UPDATE_CONNECTIONS',
        componentPointer,
        _.assign({controllersToRefresh}, options)
    )
}

const afterDisconnect = (ps: PS, componentPointer: Pointer, controllerRef: Pointer) => {
    const controllerData = dataModel.getDataItem(ps, controllerRef)
    const controllersToRefresh = getComponentsControllers(ps, componentPointer).concat(controllerData.id)
    const options = getOptionsWithAllAppsModifiers(ps, componentPointer, hooks.HOOKS.CONNECTION.AFTER_DISCONNECT)
    refreshWhenAppsNotEmpty(
        ps,
        [appFinder.getAppOfController(ps, controllerRef)],
        'AFTER_DISCONNECT',
        componentPointer,
        _.assign({controllersToRefresh}, options)
    )
}

const afterSetByPointer = (ps: PS, dataItemPointer: Pointer, data) =>
    refreshWhenAppsNotEmpty(ps, appFinder.appsOfComp(ps, data, dataItemPointer), 'SET_BY_POINTER_AFTER', null, {
        sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()}
    })

const afterAddRoot = (ps: PS, componentPointer: Pointer) => {
    refreshWhenAppsNotEmpty(ps, appFinder.appsOfComp(ps, null, componentPointer), 'ADD_ROOT.AFTER', componentPointer, {
        sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()}
    })
}

const afterAddRootFromExt = (ps: PS, data: ComponentAfterRootAddDataFromExtEvent) => {
    const {compToAddPointer} = data
    afterAddRoot(ps, compToAddPointer)
}

const slotsAfterPopulate = (
    ps: PS,
    ownerOfComponentInSlot: Pointer,
    componentWithSlots: Pointer,
    componentToAddPointer: Pointer
) => {
    refreshHostAndPluginApps(ps, componentToAddPointer, ownerOfComponentInSlot, 'SLOTS.AFTER_POPULATE')
}

function refreshHostAndPluginApps(ps: PS, pluginPointer: Pointer, hostPointer: Pointer, source: string) {
    const pluginAppDefinitionId = dataModel.getDataItem(ps, pluginPointer)?.appDefinitionId
    const hostAppDefinitionId = dataModel.getDataItem(ps, hostPointer)?.appDefinitionId
    if (pluginAppDefinitionId && hostAppDefinitionId) {
        return refreshWhenAppsNotEmpty(ps, [hostAppDefinitionId, pluginAppDefinitionId], source, hostPointer, {
            sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()}
        })
    }
}

const afterChangeCompViewMode = (ps: PS, viewMode: string) => {
    if (viewMode === 'editor') {
        refreshAllApps(ps, {
            immediate: true,
            restartWorker: true,
            skipAppsCheck: !!appFilters.hasAppsWithViewerScript(ps),
            source: 'CHANGE_COMPONENT_VIEW_MODE',
            sharedStateSyncOptions: {shouldSync: false}
        })
    } else {
        refreshAllApps(ps, {
            sendLoad: true,
            sendInitAndStart: true,
            skipAppsCheck: !!appFilters.hasAppsWithViewerScript(ps),
            source: 'CHANGE_COMPONENT_VIEW_MODE',
            sharedStateSyncOptions: {shouldSync: false}
        })
    }
}

const refreshAppsAfterChangeParent = (
    ps: PS,
    componentPointer: Pointer,
    dataItem: any,
    controllersToRefresh: any[],
    options: any
) => {
    refreshWhenAppsNotEmpty(
        ps,
        appFinder.appsOfComp(ps, dataItem, componentPointer),
        'CHANGE_PARENT',
        componentPointer,
        {
            ...options,
            controllersToRefresh,
            sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()}
        }
    )
}

const isComponentUnderSamePage = (ps: PS, newContainerPointer: Pointer, oldParentPointer: Pointer) =>
    ps.pointers.structure.getPageOfComponent(newContainerPointer)?.id !==
    ps.pointers.structure.getPageOfComponent(oldParentPointer)?.id

const afterChangeParent = (
    ps: PS,
    componentPointer: Pointer,
    newContainerPointer: Pointer,
    oldParentPointer: Pointer
) => {
    if (ps.dal.get(componentPointer)) {
        const controllersToRefresh = getComponentsControllers(ps, componentPointer)
        const options: any = getOptionsWithAllAppsModifiers(ps, componentPointer, hooks.HOOKS.CHANGE_PARENT.AFTER)
        const dataItem = dataModel.getDataItem(ps, componentPointer)
        if (!appFilters.isBlocksWidgetRef(ps, componentPointer)) {
            refreshAppsAfterChangeParent(ps, componentPointer, dataItem, controllersToRefresh, options)
        } else if (
            appFilters.isBlocksWidgetRef(ps, componentPointer) &&
            isComponentUnderSamePage(ps, newContainerPointer, oldParentPointer)
        ) {
            refreshAppsAfterChangeParent(ps, componentPointer, dataItem, controllersToRefresh, options)
        }
    } else {
        refreshAllApps(ps, {
            resetRuntime: true,
            sendInitAndStart: true,
            source: 'CHANGE_PARENT',
            sharedStateSyncOptions: {shouldSync: true, pageId: ps.siteAPI.getPrimaryPageId()}
        })
    }
}

const afterNavigateToPage = (ps: PS) =>
    refreshAllApps(ps, {
        source: 'AFTER_NAVIGATE_TO_PAGE',
        immediate: true,
        shouldRunInQueue: false,
        sharedStateSyncOptions: {shouldSync: false}
    })

const afterNavigateToPageDone = (ps: PS, pageInfo, prevPageInfo) => {
    const isDataChanged = isDynamicPageDataChanged(pageInfo, prevPageInfo)
    if (isDataChanged) {
        refreshAllApps(ps, {
            source: 'AFTER_NAVIGATE_TO_PAGE_DONE',
            immediate: true,
            sendLoad: true,
            sendInitAndStart: true,
            resetRuntime: true,
            sharedStateSyncOptions: {shouldSync: false}
        })
    }
}

const afterUpdateWixCodeModel = (ps: PS) =>
    refreshAllApps(ps, {
        source: 'WIXCODE.UPDATE_MODEL',
        immediate: true,
        sendLoad: true,
        sendInitAndStart: true,
        resetRuntime: true,
        sharedStateSyncOptions: {shouldSync: false}
    })

const afterRouterDataReloaded = (ps: PS) =>
    refreshAllApps(ps, {
        source: 'DATA_RELOADED',
        immediate: true,
        sendLoad: true,
        sendInitAndStart: true,
        resetRuntime: true,
        sharedStateSyncOptions: {shouldSync: false}
    })

const afterChangeLanguage = (ps: PS) =>
    refreshAllApps(ps, {
        source: 'AFTER_CHANGE_LANGUAGE',
        immediate: true,
        shouldRunInQueue: false,
        sharedStateSyncOptions: {shouldSync: false}
    })

const afterSwitchViewMode = (ps: PS) =>
    refreshAllApps(ps, {restartWorker: true, source: 'SWITCH_VIEW_MODE', sharedStateSyncOptions: {shouldSync: false}})

const afterAppUpdate = (ps: PS, appData: AppData) => {
    const {apps, controllersToRefresh, compsIdsToReset} = appFilters.optionsModifierSelfRefresh(
        ps,
        ps.siteAPI.getAllowedApps().filter(appDefId => appDefId === appData.appDefinitionId)
    )

    refresh(ps, {
        apps,
        restartWorker: true,
        source: 'PLATFORM.APP_UPDATED',
        controllersToRefresh,
        compsIdsToReset,
        shouldFetchData: true,
        sharedStateSyncOptions: {shouldSync: true}
    })
}

const afterApplySnapshot = (ps: PS) => {
    const {apps, controllersToRefresh, compsIdsToReset} = appFilters.optionsModifierSelfRefresh(
        ps,
        ps.siteAPI.getAllowedApps()
    )

    refresh(ps, {
        apps,
        restartWorker: true,
        source: 'UNDO_REDO.AFTER_APPLY_SNAPSHOT',
        controllersToRefresh,
        compsIdsToReset,
        shouldFetchData: true,
        sharedStateSyncOptions: {shouldSync: true}
    })
}

const afterImport = (ps: PS) => {
    const {apps, controllersToRefresh, compsIdsToReset} = appFilters.optionsModifierSelfRefresh(
        ps,
        ps.siteAPI.getAllowedApps()
    )

    refresh(ps, {
        apps,
        restartWorker: true,
        source: 'IMPORT_EXPORT.AFTER_IMPORT',
        controllersToRefresh,
        compsIdsToReset,
        shouldFetchData: true,
        sharedStateSyncOptions: {shouldSync: true}
    })
}

const refreshAppsInternalListener = (ps, livePreviewRefreshOptions) => {
    try {
        const syncByPageId = _.get(livePreviewRefreshOptions, ['sharedStateSyncOptions', 'pageId'])
        const currentPageId = ps.siteAPI.getPrimaryPageId()
        if (!syncByPageId || syncByPageId === currentPageId || syncByPageId === 'masterPage') {
            refreshAppsInternal(ps, livePreviewRefreshOptions)
        }
    } catch (e) {
        console.error(e)
        ps.logger.captureError(e as Error, {
            tags: {
                livePreviewRefreshError: true
            }
        })
    }
}

function autoRun(ps: PS) {
    const {distributor} = ps.extensionAPI as DistributorExtensionAPI
    distributor.subscribeToMessage<LivePreviewSharedStateMessage>(
        'livePreviewSharedState',
        ({data: {livePreviewRefreshOptions}}) => refreshAppsInternalListener(ps, livePreviewRefreshOptions)
    )
    unregisterHooks()

    registerHook(hooks.HOOKS.DATA.UPDATE_AFTER, afterDataUpdate)

    registerHook(hooks.HOOKS.PROPERTIES.UPDATE_AFTER, afterPropertiesUpdate)

    registerHook(hooks.HOOKS.DATA.AFTER_UPDATE_CONNECTIONS, afterConnectionsUpdate)

    registerHook(hooks.HOOKS.CONNECTION.AFTER_DISCONNECT, afterDisconnect)

    registerHook(hooks.HOOKS.DATA.SET_BY_POINTER_AFTER, afterSetByPointer)

    registerHook(hooks.HOOKS.ADD_ROOT.AFTER, afterAddRoot)

    registerHookWithPs(ps, COMPONENTS_HOOKS.ADD_ROOT.AFTER_FROM_EXT.id, afterAddRootFromExt)
    registerHook(hooks.HOOKS.SLOTS.AFTER_POPULATE, slotsAfterPopulate)
    registerHook(hooks.HOOKS.CHANGE_PARENT.AFTER, afterChangeParent)
    registerHook(hooks.HOOKS.ROUTER.DATA_RELOADED, afterRouterDataReloaded)

    registerHook(hooks.HOOKS.UNDO_REDO.AFTER_APPLY_SNAPSHOT, afterApplySnapshot)
    registerHook(hooks.HOOKS.IMPORT_EXPORT.AFTER_IMPORT, afterImport)

    if (!experiment.isOpen('dm_removeLivePreviewHooksNoNeeded')) {
        registerHook(hooks.HOOKS.CHANGE_COMPONENT_VIEW_MODE.AFTER, afterChangeCompViewMode)
        registerHook(hooks.HOOKS.SWITCH_VIEW_MODE.AFTER, afterSwitchViewMode)
        registerHook(hooks.HOOKS.PAGE.AFTER_NAVIGATE_TO_PAGE, afterNavigateToPage)
        registerHook(hooks.HOOKS.PAGE.AFTER_NAVIGATE_TO_PAGE_DONE, afterNavigateToPageDone)
        registerHook(hooks.HOOKS.MULTILINGUAL.AFTER_CHANGE_LANGUAGE, afterChangeLanguage)
        registerHook(hooks.HOOKS.WIXCODE.UPDATE_MODEL, afterUpdateWixCodeModel)
    }

    registerHook(hooks.HOOKS.PLATFORM.APP_UPDATED, afterAppUpdate)
}

const toggleLivePreviewEnabled = (ps, isEnabled: boolean) => {
    ps.siteAPI.toggleLivePreviewEnabled(isEnabled)
    if (isEnabled) {
        refreshAllApps(ps, {
            immediate: true,
            shouldRunInQueue: false
        })
    }
}

export default {
    debouncedRefresh: refreshAppsAPI,
    debouncedRefreshComponent: refreshComponentAppAPI,
    updateWidgetViewState,
    updateWidgetViewStateAfterRefresh,
    isActive,
    autoRun,
    toggleLivePreviewEnabled
}
